<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import format from 'date-fns/format';
import DatePicker from 'vue-datepicker-next';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
});

const dueAt = defineModel('dueAt', {
  type: Date,
  default: null,
});
const assignee = defineModel('assignee', {
  type: Object,
  default: null,
});
const { t } = useI18n();

const assigneeOptions = computed(() => [
  {
    id: null,
    name: t('CONVERSATION.REPLYBOX.TASK_FORM.ALL_ASSIGNEES'),
  },
  ...props.agents.map(agent => ({
    id: agent.id,
    name: agent.name,
  })),
]);

const atNoon = date => {
  const value = new Date(date);
  value.setHours(12, 0, 0, 0);
  return value;
};

const dueDateOptions = computed(() => [
  {
    id: 'today',
    name: t('CONVERSATION.REPLYBOX.TASK_FORM.DUE_DATE.TODAY'),
    date: atNoon(new Date()),
  },
  {
    id: 'tomorrow',
    name: t('CONVERSATION.REPLYBOX.TASK_FORM.DUE_DATE.TOMORROW'),
    date: atNoon(addDays(new Date(), 1)),
  },
  {
    id: 'in_2_days',
    name: t('CONVERSATION.REPLYBOX.TASK_FORM.DUE_DATE.IN_2_DAYS'),
    date: atNoon(addDays(new Date(), 2)),
  },
  {
    id: 'next_week',
    name: t('CONVERSATION.REPLYBOX.TASK_FORM.DUE_DATE.NEXT_WEEK'),
    date: atNoon(addDays(new Date(), 7)),
  },
]);

const selectedPresetId = computed(() => {
  const preset = dueDateOptions.value.find(
    option => dueAt.value && isSameDay(dueAt.value, option.date)
  );
  return preset?.id || 'custom';
});

const isPickingCustomDate = ref(false);

const dueDateLabel = computed(() => {
  if (selectedPresetId.value !== 'custom') {
    return dueDateOptions.value.find(
      option => option.id === selectedPresetId.value
    ).name;
  }
  return dueAt.value ? format(dueAt.value, 'dd/MM/yyyy') : '';
});

const selectPreset = option => {
  dueAt.value = option.date;
  isPickingCustomDate.value = false;
};

const disableBeforeToday = date => atNoon(date) < atNoon(new Date());

const selectCustomDate = (value, closeMenu) => {
  if (!value) return;
  dueAt.value = atNoon(value);
  isPickingCustomDate.value = false;
  closeMenu();
};

const onTriggerClick = (isOpen, toggle) => {
  if (!isOpen) isPickingCustomDate.value = false;
  toggle();
};
</script>

<template>
  <div class="task-composer-toolbar">
    <SingleSelect
      v-model="assignee"
      :options="assigneeOptions"
      :placeholder="t('CONVERSATION.REPLYBOX.TASK_FORM.ASSIGNEE_PLACEHOLDER')"
      placeholder-icon="i-lucide-user-round"
      variant="faded"
      open-top
    />
    <DropdownContainer open-top class="task-date-field-wrap">
      <template #trigger="{ isOpen, toggle }">
        <button
          type="button"
          class="task-date-field"
          :aria-label="t('CONVERSATION.REPLYBOX.TASK_FORM.DATE')"
          @click="onTriggerClick(isOpen, toggle)"
        >
          <span class="i-lucide-calendar-days size-4 text-n-slate-10" />
          <span class="truncate">{{ dueDateLabel }}</span>
        </button>
      </template>
      <template #default="{ closeMenu }">
        <DropdownBody class="bottom-0 min-w-56 z-50" strong>
          <DropdownSection v-if="!isPickingCustomDate">
            <DropdownItem
              v-for="option in dueDateOptions"
              :key="option.id"
              :click="() => selectPreset(option)"
            >
              <template #label>
                <span class="flex w-full items-center justify-between">
                  {{ option.name }}
                  <Icon
                    v-if="selectedPresetId === option.id"
                    icon="i-lucide-check"
                    class="bg-n-blue-text pointer-events-none"
                  />
                </span>
              </template>
            </DropdownItem>
            <DropdownItem
              preserve-open
              :click="() => (isPickingCustomDate = true)"
            >
              <template #label>
                <span class="flex w-full items-center justify-between">
                  {{ t('CONVERSATION.REPLYBOX.TASK_FORM.DUE_DATE.CUSTOM') }}
                  <Icon
                    v-if="selectedPresetId === 'custom'"
                    icon="i-lucide-check"
                    class="bg-n-blue-text pointer-events-none"
                  />
                </span>
              </template>
            </DropdownItem>
          </DropdownSection>
          <DatePicker
            v-else
            type="date"
            inline
            :clearable="false"
            :editable="false"
            :value="dueAt"
            :disabled-date="disableBeforeToday"
            @change="value => selectCustomDate(value, closeMenu)"
          />
        </DropdownBody>
      </template>
    </DropdownContainer>
  </div>
</template>

<style scoped lang="scss">
.task-composer-toolbar {
  @apply flex w-full items-center gap-2;
}

.task-composer-toolbar :deep(.multiselect-wrap) {
  @apply min-w-36 flex-1 rounded-full border border-n-weak bg-n-alpha-2;
}

.task-date-field-wrap {
  @apply min-w-40;
}

.task-date-field {
  @apply flex h-9 w-full min-w-40 items-center gap-2 rounded-full border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12;
}
</style>
